import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "../../../app/providers/AuthProvider";
import { resolveStaffRoles } from "../permissions";

/**
 * RequireSuperAdmin — gating para rutas /superadmin/*.
 *
 * Verifica is_staff del objeto user (TASK-02 lo expone en /users/me/)
 * o role === 'SUPERADMIN' desde el JWT claim (TASK-17 puede emitirlo).
 * Ambas vías son aceptadas; el backend siempre valida IsSuperAdmin server-side.
 *
 * No modifica RequireRole (src/app/router/RequireRole.js) porque ése
 * no tiene acceso a user.is_staff — solo lee el claim `role`.
 *
 * @param {React.ReactNode} [children] Envuelve un elemento; si se omite, actúa
 *   como ruta de layout renderizando <Outlet/>.
 * @param {string[]} [requiredRoles] Roles de staff de plataforma (TASK-20)
 *   admitidos en esta ruta, p. ej. `["PLATFORM_ADMIN"]`. Semántica **any-of**:
 *   basta con tener UNO (los permisos del backend son la unión de los roles, así
 *   que exigirlos todos dejaría fuera a quien tiene de más). Omitido o vacío →
 *   solo se exige ser staff, igual que antes de TASK-22.
 *   Es gating de UX: el backend responde 403 `staff_permission_denied` igual.
 */
function RequireSuperAdmin({ children, requiredRoles }) {
  const { isAuthenticated, role, user, isLoading } = useAuth();

  if (isLoading) return null;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = role === "SUPERADMIN" || user?.is_staff === true;
  if (!isSuperAdmin) {
    // El JWT no emite `is_staff`: AuthProvider lo completa con una petición a
    // /users/me/ inmediatamente después del login. Mientras esa petición está
    // en vuelo el valor es DESCONOCIDO (`undefined`, que no es lo mismo que
    // `false`) y denegar sería un falso negativo: el staff aterrizaría en
    // /unauthorized nada más entrar y, al ser un redirect `replace`, nada lo
    // devolvería cuando llega la respuesta. Se espera un render; la petición
    // siempre resuelve, incluso si falla (su .catch deja staff_roles = []).
    if (user?.is_staff === undefined) return null;
    return <Navigate to="/unauthorized" replace />;
  }

  if (requiredRoles?.length) {
    const held = resolveStaffRoles(user);
    if (!requiredRoles.some((r) => held.includes(r))) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children ? <>{children}</> : <Outlet />;
}

export { RequireSuperAdmin };
